import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Navigation, Waves } from "lucide-react";

type Spot = {
  id: string;
  name: string;
  lat: number;
  lon: number;
  notes: string | null;
  photo_path: string | null;
};

const SharedSpot = () => {
  const { token } = useParams<{ token: string }>();
  const [spot, setSpot] = useState<Spot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const { data } = await supabase
        .from("favourite_spots")
        .select("id,name,lat,lon,notes,photo_path")
        .eq("share_token", token)
        .maybeSingle();
      setSpot(data as Spot | null);
      setLoading(false);
      if (data) document.title = `${data.name} — shared spot · Swell Atlas`;
    })();
  }, [token]);

  const photoUrl = spot?.photo_path
    ? supabase.storage.from("favourite-spot-photos").getPublicUrl(spot.photo_path).data.publicUrl
    : null;

  return (
    <main className="min-h-screen container max-w-2xl py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-primary mb-6">
        <Waves className="h-3.5 w-3.5" /> Swell Atlas
      </Link>

      {loading ? (
        <div className="rounded-3xl bg-card/80 border border-border p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
        </div>
      ) : !spot ? (
        <div className="rounded-3xl bg-card/80 border border-border p-12 text-center space-y-3">
          <h1 className="font-display text-2xl">Spot not found</h1>
          <p className="text-muted-foreground">This share link is invalid or has been removed.</p>
          <Button asChild><Link to="/">Back to Swell Atlas</Link></Button>
        </div>
      ) : (
        <article className="rounded-3xl border border-border bg-card/80 backdrop-blur overflow-hidden">
          {photoUrl && <img src={photoUrl} alt={spot.name} className="w-full aspect-video object-cover" />}
          <div className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Shared spot</div>
              <h1 className="font-display text-3xl mt-1">{spot.name}</h1>
              <div className="text-sm text-muted-foreground inline-flex items-center gap-1 mt-1">
                <MapPin className="h-3.5 w-3.5" /> {spot.lat.toFixed(4)}, {spot.lon.toFixed(4)}
              </div>
            </div>
            {spot.notes && <p className="text-foreground/90 whitespace-pre-wrap">{spot.notes}</p>}
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" className="flex-1 min-w-[10rem]">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${spot.lat},${spot.lon}&travelmode=driving`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Navigation className="h-4 w-4" /> <span className="ml-1.5">Directions</span>
                </a>
              </Button>
              <Button asChild className="flex-1 min-w-[10rem] bg-gradient-sunset text-accent-foreground hover:opacity-90">
                <Link to="/">Open Swell Atlas</Link>
              </Button>
            </div>
          </div>
        </article>
      )}
    </main>
  );
};

export default SharedSpot;